import {
  createEmptyColumn,
  createEmptyGrid,
  createEmptyRow,
  findNodeById,
  findRowById,
  rebalanceRowHeights,
} from "./documentModel";
import { getComponentPreset } from "./defaultComponents";
import { getColumnSlotState } from "./columnContentUtils";
import { cloneDeep, createId, regenerateGridIds } from "./idUtils";

const touchDocument = (document, updater) => ({
  ...document,
  ...updater(document),
  updatedAt: new Date().toISOString(),
});

const updateLayout = (document, updater) =>
  touchDocument(document, (current) => ({
    layout: updater(cloneDeep(current.layout)),
  }));

const rebalanceColumnWidths = (columns) => {
  if (!columns?.length) {
    return;
  }
  const width = Math.floor(100 / columns.length);
  const remainder = 100 - width * columns.length;
  columns.forEach((column, index) => {
    column.widthPercent = width + (index === 0 ? remainder : 0);
  });
};

const nextLayoutName = (layout, prefix = "Layout") => {
  const count = (layout.grids || []).length + 1;
  return `${prefix} ${count}`;
};

const nextNestedLayoutName = (column) => {
  const nestedCount = (column.children || []).filter((child) => child.type === "grid").length + 1;
  return `Layout interno ${nestedCount}`;
};

export const createComponentInstance = (componentKey, propsOverride = null) => {
  const preset = getComponentPreset(componentKey);
  return {
    type: "component",
    id: createId("cmp"),
    componentKey,
    props: propsOverride ? cloneDeep(propsOverride) : cloneDeep(preset.defaultProps),
    overrides: { html: null, css: null },
  };
};

/**
 * @returns {"inserted" | "replaced" | "blocked_nested_grid" | "blocked" | "not_found"}
 */
export const insertComponentInColumn = (
  document,
  columnId,
  componentKey,
  propsOverride = null,
  { replace = false } = {}
) => {
  let result = "not_found";
  const next = updateLayout(document, (layout) => {
    const found = findNodeById(layout, columnId);
    if (!found || found.nodeType !== "column") {
      return layout;
    }
    const slot = getColumnSlotState(found.node);
    if (slot.hasNestedGrid) {
      result = "blocked_nested_grid";
      return layout;
    }
    if (slot.hasComponent && !replace) {
      result = "blocked";
      return layout;
    }
    const instance = createComponentInstance(componentKey, propsOverride);
    found.node.children = [instance];
    result = slot.hasComponent ? "replaced" : "inserted";
    return layout;
  });
  return { document: next, result };
};

export const updateNodeProps = (document, nodeId, propsPatch) =>
  updateLayout(document, (layout) => {
    const found = findNodeById(layout, nodeId);
    if (!found) {
      return layout;
    }
    let patch = propsPatch;
    if (propsPatch.widthPercent !== undefined && found.nodeType === "column") {
      found.node.widthPercent = propsPatch.widthPercent;
      const { widthPercent, ...rest } = propsPatch;
      patch = rest;
    }
    if (Object.keys(patch).length) {
      found.node.props = { ...(found.node.props || {}), ...patch };
    }
    return layout;
  });

export const updateGridName = (document, gridId, name) =>
  updateLayout(document, (layout) => {
    const found = findNodeById(layout, gridId);
    if (!found || found.nodeType !== "grid") {
      return layout;
    }
    found.node.name = name;
    return layout;
  });

export const updateComponentProps = (document, componentId, propsPatch) =>
  updateLayout(document, (layout) => {
    const found = findNodeById(layout, componentId);
    if (!found || found.nodeType !== "component") {
      return layout;
    }
    found.node.props = { ...(found.node.props || {}), ...propsPatch };
    return layout;
  });

const removeFromGrid = (grid, nodeId) => {
  grid.rows = (grid.rows || [])
    .map((row) => {
      row.columns = (row.columns || []).filter((column) => {
        if (column.id === nodeId) {
          return false;
        }
        column.children = (column.children || []).filter((child) => {
          if (child.id === nodeId) {
            return false;
          }
          if (child.type === "grid") {
            removeFromGrid(child, nodeId);
          }
          return true;
        });
        return true;
      });
      rebalanceColumnWidths(row.columns);
      return row;
    })
    .filter((row) => (row.columns || []).length > 0);
};

export const clearColumnContent = (document, columnId) =>
  updateLayout(document, (layout) => {
    const found = findNodeById(layout, columnId);
    if (!found || found.nodeType !== "column") {
      return layout;
    }
    found.node.children = [];
    return layout;
  });

export const removeNode = (document, nodeId) =>
  updateLayout(document, (layout) => {
    if (layout.id === nodeId) {
      return layout;
    }
    layout.grids = (layout.grids || []).filter((grid) => {
      if (grid.id === nodeId) {
        return false;
      }
      removeFromGrid(grid, nodeId);
      return true;
    });
    return layout;
  });

export const appendLayoutBlock = (document, savedGrid) =>
  updateLayout(document, (layout) => {
    const grid = regenerateGridIds(savedGrid);
    if (!grid.name) {
      grid.name = nextLayoutName(layout);
    }
    layout.grids = [...(layout.grids || []), grid];
    return layout;
  });

/** Reorder top-level layout blocks only (direction: -1 up, +1 down). */
export const moveTopLevelLayoutGrid = (document, gridId, direction) =>
  updateLayout(document, (layout) => {
    const grids = [...(layout.grids || [])];
    const index = grids.findIndex((grid) => grid.id === gridId);
    if (index < 0) {
      return layout;
    }
    const target = index + direction;
    if (target < 0 || target >= grids.length) {
      return layout;
    }
    const [item] = grids.splice(index, 1);
    grids.splice(target, 0, item);
    return { ...layout, grids };
  });

export const replaceDocumentMeta = (document, patch) => ({
  ...document,
  ...patch,
  updatedAt: new Date().toISOString(),
});

export const addGridToLayout = (document, rowCount = 1, columnCount = 1) =>
  updateLayout(document, (layout) => {
    layout.grids = [
      ...(layout.grids || []),
      createEmptyGrid(rowCount, columnCount, nextLayoutName(layout)),
    ];
    return layout;
  });

export const insertNestedLayoutInColumn = (document, columnId, savedGrid, { replace = false } = {}) => {
  let result = "not_found";
  const next = updateLayout(document, (layout) => {
    const found = findNodeById(layout, columnId);
    if (!found || found.nodeType !== "column") {
      return layout;
    }
    const slot = getColumnSlotState(found.node);
    if (slot.hasNestedGrid && !replace) {
      result = "blocked";
      return layout;
    }
    if (slot.hasComponent && !replace) {
      result = "blocked";
      return layout;
    }
    const grid = regenerateGridIds(savedGrid);
    if (!grid.name) {
      grid.name = nextNestedLayoutName(found.node);
    }
    found.node.children = [grid];
    result = slot.hasNestedGrid || slot.hasComponent ? "replaced" : "inserted";
    return layout;
  });
  return { document: next, result };
};

export const addNestedGridToColumn = (document, columnId, rowCount = 1, columnCount = 1) => {
  const { document: next } = insertNestedLayoutInColumn(
    document,
    columnId,
    createEmptyGrid(rowCount, columnCount, "Layout interno"),
    { replace: false }
  );
  return next;
};

export const addRowToGrid = (document, gridId) =>
  updateLayout(document, (layout) => {
    const found = findNodeById(layout, gridId);
    if (!found || found.nodeType !== "grid") {
      return layout;
    }
    const rows = [...(found.node.rows || []), createEmptyRow(1)];
    rebalanceRowHeights(rows);
    found.node.rows = rows;
    return layout;
  });

export const addColumnToRow = (document, rowId) =>
  updateLayout(document, (layout) => {
    const found = findRowById(layout, rowId);
    if (!found) {
      return layout;
    }
    const columns = found.row.columns || [];
    const nextCount = columns.length + 1;
    const width = Math.floor(100 / nextCount);
    const remainder = 100 - width * nextCount;
    found.row.columns = [
      ...columns.map((column, index) => ({
        ...column,
        widthPercent: width + (index === 0 ? remainder : 0),
      })),
      createEmptyColumn(width),
    ];
    return layout;
  });

export const moveLayoutBlock = (document, gridId, direction) =>
  updateLayout(document, (layout) => {
    const grids = layout.grids || [];
    const index = grids.findIndex((grid) => grid.id === gridId);
    if (index < 0) {
      return layout;
    }
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= grids.length) {
      return layout;
    }
    const next = [...grids];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    layout.grids = next;
    return layout;
  });

export const resizeAdjacentColumns = (document, rowId, leftColumnId, rightColumnId, leftWidth, rightWidth) =>
  updateLayout(document, (layout) => {
    const found = findRowById(layout, rowId);
    if (!found) {
      return layout;
    }
    const grid = found.grid;
    const columnIndex = (found.row.columns || []).findIndex((column) => column.id === leftColumnId);
    if (columnIndex < 0) {
      return layout;
    }
    for (const row of grid.rows || []) {
      const columns = row.columns || [];
      if (columnIndex >= columns.length - 1) {
        continue;
      }
      columns[columnIndex].widthPercent = Math.round(leftWidth * 10) / 10;
      columns[columnIndex + 1].widthPercent = Math.round(rightWidth * 10) / 10;
    }
    return layout;
  });

export const setColumnWidthInRow = (document, rowId, columnId, widthPercent) =>
  updateLayout(document, (layout) => {
    const found = findRowById(layout, rowId);
    if (!found) {
      return layout;
    }
    const columns = found.row.columns || [];
    if (columns.length === 1) {
      columns[0].widthPercent = widthPercent;
      return layout;
    }
    const target = columns.find((column) => column.id === columnId);
    if (!target) {
      return layout;
    }
    const others = columns.filter((column) => column.id !== columnId);
    const remaining = Math.max(0, 100 - widthPercent);
    const share = others.length ? remaining / others.length : 0;
    target.widthPercent = widthPercent;
    others.forEach((column) => {
      column.widthPercent = Math.round(share * 10) / 10;
    });
    return layout;
  });

export const resizeGrid = (document, gridId, rowCount, columnCount) =>
  updateLayout(document, (layout) => {
    const found = findNodeById(layout, gridId);
    if (!found || found.nodeType !== "grid") {
      return layout;
    }
    const oldRows = found.node.rows || [];
    const width = Math.floor(100 / columnCount);
    const remainder = 100 - width * columnCount;
    const rowHeight = Math.floor(100 / rowCount);
    const rowRemainder = 100 - rowHeight * rowCount;
    found.node.rows = Array.from({ length: rowCount }, (_, rowIndex) => {
      const oldRow = oldRows[rowIndex];
      const oldColumns = oldRow?.columns || [];
      return {
        id: oldRow?.id || createId("row"),
        heightPercent: oldRow?.heightPercent ?? rowHeight + (rowIndex === 0 ? rowRemainder : 0),
        columns: Array.from({ length: columnCount }, (_, colIndex) => {
          const oldColumn = oldColumns[colIndex];
          if (oldColumn) {
            return {
              ...oldColumn,
              widthPercent: width + (colIndex === 0 ? remainder : 0),
            };
          }
          return createEmptyColumn(width + (colIndex === 0 ? remainder : 0));
        }),
      };
    });
    return layout;
  });

export const extractLayoutBlockForSave = (grid) => cloneDeep(grid);

export const buildSavedComponentPayload = (name, component) => ({
  name,
  componentKey: component.componentKey,
  props: cloneDeep(component.props || {}),
});
 